import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, Cookie, Croissant, MapPin, Truck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { NewsletterForm } from "@/components/NewsletterForm";
import { useSite } from "@/lib/SiteContext";
import { CATEGORY_IMAGES, GRAZING_IMAGE, HERO_IMAGE } from "@/lib/images";

const STEPS = [
  { n: "01", title: "Browse this week's bakes", text: "Our menu changes with the seasons — pick your favourites from brownies, cookies, dough and more." },
  { n: "02", title: "Order by Wednesday, 6pm", text: "Every week's orders close at 6:00pm on Wednesday, so we know exactly what to bake." },
  { n: "03", title: "We bake fresh for you", text: "Everything is handmade in small batches — never baked before you order." },
  { n: "04", title: "Collect or local delivery", text: "Choose a collection slot or local delivery at checkout. Collection is always free." },
];

export default function Home() {
  const { site } = useSite();
  const categories = site?.categories || [];
  const testimonials = site?.testimonials || [];
  const c = site?.content?.home || {};

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(232,122,36,0.14),transparent)]" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-14 text-center sm:px-6 sm:pb-24 sm:pt-20 lg:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold" data-testid="hero-overline">
              Handmade with love in the UK
            </p>
            <h1 className="mt-6" data-testid="hero-heading">
              <img src="/assets/logo.png" alt="Bakes by CR" className="mx-auto w-72 sm:w-96 lg:w-[28rem]" data-testid="hero-logo" />
            </h1>
            <p className="mx-auto mt-4 max-w-xl font-serif text-xl italic text-cream-muted sm:text-2xl" data-testid="hero-tagline">
              {c.hero_subtitle || "Handmade Bakes & Treats"}
            </p>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-cream-muted sm:text-lg">
              {c.hero_title || "From brownies to cookies, seasonal bakes & more — all handmade by us."}
            </p>
            {c.ordering_note && (
              <p className="mx-auto mt-3 max-w-xl text-sm font-semibold text-gold" data-testid="hero-ordering-note">{c.ordering_note}</p>
            )}
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/shop"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-3.5 text-base font-bold text-choco transition-colors duration-150 hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus focus-visible:ring-offset-2 focus-visible:ring-offset-choco active:scale-[0.98] sm:w-auto"
                data-testid="hero-shop-now-button"
              >
                Shop This Week's Bakes
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/events"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-choco-line px-8 py-3.5 text-base font-bold text-cream-text transition-colors duration-150 hover:border-gold hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus focus-visible:ring-offset-2 focus-visible:ring-offset-choco sm:w-auto"
                data-testid="hero-events-button"
              >
                Plan an Event
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="mx-auto mt-12 max-w-4xl">
            <div className="overflow-hidden rounded-3xl border border-choco-line shadow-2xl shadow-black/50">
              <img
                src={HERO_IMAGE.url}
                alt={HERO_IMAGE.alt}
                className="aspect-[16/9] w-full object-cover sm:aspect-[21/9]"
                loading="eager"
                data-testid="hero-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8" aria-labelledby="categories-heading">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Our Products</p>
          <h2 id="categories-heading" className="mt-3 font-serif text-2xl font-bold tracking-tight text-cream-text sm:text-3xl lg:text-4xl">
            {c.intro_heading || "Reimagine the Art of Baking"}
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((cat, i) => {
            const img = CATEGORY_IMAGES[cat.slug];
            return (
              <Reveal key={cat.slug} delay={i * 0.06}>
                <Link
                  to={`/shop?category=${cat.slug}`}
                  className="group block overflow-hidden rounded-3xl bg-cream text-cream-darktext transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus"
                  data-testid={`category-card-${cat.slug}`}
                >
                  {img && (
                    <div className="overflow-hidden">
                      <img src={img.url} alt={img.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-6 sm:p-7">
                    <h3 className="font-serif text-xl font-semibold sm:text-2xl">{cat.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-cream-darkmuted">{cat.description}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-gold-hover">
                      Browse <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* How ordering works */}
      <section className="border-y border-choco-line bg-choco-light" aria-labelledby="how-heading">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">How Ordering Works</p>
            <h2 id="how-heading" className="mt-3 font-serif text-2xl font-bold tracking-tight text-cream-text sm:text-3xl lg:text-4xl">
              Fresh bakes, every week
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06}>
                <div className="h-full rounded-3xl border border-choco-line bg-choco p-6 sm:p-8" data-testid={`how-step-${s.n}`}>
                  <span className="font-serif text-3xl font-bold text-gold" aria-hidden="true">{s.n}</span>
                  <h3 className="mt-4 text-lg font-semibold text-cream-text">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-cream-muted">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery & collection */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8" aria-labelledby="fulfilment-heading">
        <Reveal>
          <h2 id="fulfilment-heading" className="font-serif text-2xl font-bold tracking-tight text-cream-text sm:text-3xl lg:text-4xl">
            Delivery or collection — your choice
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl bg-cream p-6 text-cream-darktext sm:p-10" data-testid="delivery-card">
              <Truck className="h-8 w-8 text-gold" aria-hidden="true" />
              <h3 className="mt-5 font-serif text-xl font-semibold sm:text-2xl">Local delivery</h3>
              <p className="mt-3 text-sm leading-relaxed text-cream-darkmuted sm:text-base">
                We deliver locally on set days each week. Enter your postcode at checkout to check we cover your
                address and see any delivery fee before you pay — free delivery may apply over a qualifying spend.
              </p>
              <Link to="/delivery-collection" className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-gold-hover hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus rounded" data-testid="delivery-info-link">
                Delivery details <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-3xl bg-cream p-6 text-cream-darktext sm:p-10" data-testid="collection-card">
              <MapPin className="h-8 w-8 text-gold" aria-hidden="true" />
              <h3 className="mt-5 font-serif text-xl font-semibold sm:text-2xl">Collection — always free</h3>
              <p className="mt-3 text-sm leading-relaxed text-cream-darkmuted sm:text-base">
                Prefer to pop by? Choose a collection slot at checkout and pay online, or select
                “pay on collection” and settle up when you pick up your bakes.
              </p>
              <Link to="/delivery-collection" className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-gold-hover hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus rounded" data-testid="collection-info-link">
                Collection details <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Grazing tables */}
      <section className="border-y border-choco-line bg-choco-light" aria-labelledby="grazing-heading">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-choco-line">
              <img src={GRAZING_IMAGE.url} alt={GRAZING_IMAGE.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" data-testid="grazing-image" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Events &amp; Grazing Tables</p>
            <h2 id="grazing-heading" className="mt-3 font-serif text-2xl font-bold tracking-tight text-cream-text sm:text-3xl lg:text-4xl">
              Liven up your next event
            </h2>
            <p className="mt-4 text-base leading-relaxed text-cream-muted">
              From birthdays to office gatherings, our curated sweet-treat grazing tables are made to impress.
              Tell us your date, venue and guest count and we'll put together something special.
            </p>
            <Link
              to="/events"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-bold text-choco transition-colors duration-150 hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus focus-visible:ring-offset-2 focus-visible:ring-offset-choco active:scale-[0.98]"
              data-testid="grazing-enquire-button"
            >
              Enquire About Your Event <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Story snippet */}
      <section className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-24" aria-labelledby="story-heading">
        <Reveal>
          <Croissant className="mx-auto h-8 w-8 text-gold" aria-hidden="true" />
          <h2 id="story-heading" className="mt-5 font-serif text-2xl font-bold tracking-tight text-cream-text sm:text-3xl">
            Our Story
          </h2>
          <p className="mt-4 text-base leading-relaxed text-cream-muted sm:text-lg">
            We started making Christmas treats for family and friends, sharing the love with every bite.
            Now we want to share it with you. Our chef Francisco brings over 20 years of experience to the
            team — along with a real sweet tooth.
          </p>
          <Link to="/about" className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-gold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus rounded" data-testid="story-read-more-link">
            Read our story <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </section>

      {/* Testimonials — only shown once genuine reviews are added in admin */}
      {testimonials.length > 0 && (
        <section className="border-y border-choco-line bg-choco-light" aria-labelledby="testimonials-heading">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
            <Reveal>
              <h2 id="testimonials-heading" className="font-serif text-2xl font-bold tracking-tight text-cream-text sm:text-3xl">
                What Our Fans Say
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
              {testimonials.map((t, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <figure className="h-full rounded-3xl bg-cream p-6 text-cream-darktext sm:p-8" data-testid={`testimonial-${i}`}>
                    <blockquote className="text-sm leading-relaxed text-cream-darkmuted sm:text-base">“{t.quote}”</blockquote>
                    <figcaption className="mt-4 text-sm font-bold">{t.name}</figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8" aria-labelledby="newsletter-heading">
        <Reveal>
          <div className="rounded-3xl bg-cream p-6 text-cream-darktext sm:p-10 lg:p-14">
            <div className="mx-auto max-w-xl text-center">
              <Cookie className="mx-auto h-8 w-8 text-gold" aria-hidden="true" />
              <h2 id="newsletter-heading" className="mt-4 font-serif text-2xl font-bold sm:text-3xl">
                Join our newsletter
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-cream-darkmuted sm:text-base">
                Recipes and exclusive offers, straight to your inbox. No spam — ever.
              </p>
              <div className="mt-6 text-left [&_input]:border-cream-darkmuted/30 [&_input]:bg-white [&_input]:text-cream-darktext [&_label]:text-cream-darkmuted">
                <NewsletterForm />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Contact CTA */}
      <section className="border-t border-choco-line" aria-labelledby="cta-heading">
        <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
          <Reveal>
            <CalendarClock className="mx-auto h-8 w-8 text-gold" aria-hidden="true" />
            <h2 id="cta-heading" className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream-text sm:text-3xl">
              Questions about an order or event?
            </h2>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-cream-muted sm:text-base">
              We're always happy to talk bakes. Get in touch and we'll come back to you as soon as we can.
            </p>
            <Link
              to="/contact"
              className="mt-7 inline-flex items-center gap-2 rounded-full border border-gold px-7 py-3 text-sm font-bold text-gold transition-colors duration-150 hover:bg-gold hover:text-choco focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus focus-visible:ring-offset-2 focus-visible:ring-offset-choco"
              data-testid="contact-cta-button"
            >
              Contact Us <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
